import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { environment } from '@env';

import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

export type SessionEndReason = 'idle' | 'expired' | 'manual';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'];
const ACTIVITY_KEY = 'arq.session.lastActivity';
const CHANNEL_NAME = 'arq-session';
const TICK_MS = 1000;
const ACTIVITY_THROTTLE_MS = 1000;

const END_MESSAGES: Record<SessionEndReason, string | null> = {
  idle: 'auth.session.endedByIdle',
  expired: 'auth.session.expired',
  manual: null,
};

/**
 * Owns the authenticated session lifecycle on the client:
 * - inactivity: no user input for `idleTimeoutMinutes` ends the session; `warningSeconds` before
 *   that, `warningSecondsLeft` turns non-null so the UI can ask "¿Sigues ahí?";
 * - keep-alive: while the user is active the refresh token is renewed every `keepAliveMinutes`, so
 *   the BE idle window (JWT_REFRESH_TTL_SEC) never expires under an active user;
 * - single exit point (`end`) used by idle, expired refresh and manual logout, synced across tabs.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private readonly timeoutMs = environment.session.idleTimeoutMinutes * 60_000;
  private readonly warningMs = environment.session.warningSeconds * 1000;
  private readonly keepAliveMs = environment.session.keepAliveMinutes * 60_000;

  private readonly _warningSecondsLeft = signal<number | null>(null);
  /** Seconds left before the idle logout while the warning is due; `null` otherwise. */
  readonly warningSecondsLeft = this._warningSecondsLeft.asReadonly();

  private lastActivity = Date.now();
  private lastKeepAlive = Date.now();
  private timer: ReturnType<typeof setInterval> | null = null;
  private ending = false;
  private readonly channel =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
  private readonly onActivity = (): void => this.recordActivity();

  constructor() {
    this.channel?.addEventListener(
      'message',
      (event: MessageEvent<{ reason?: SessionEndReason }>) => {
        if (event.data?.reason) void this.end(event.data.reason, { fromOtherTab: true });
      },
    );
    effect(() => {
      const signedIn = this.auth.isAuthenticated();
      untracked(() => (signedIn ? this.start() : this.stop()));
    });
  }

  /** "Seguir conectado": resets the idle clock and renews the server session right away. */
  continueSession(): void {
    this._warningSecondsLeft.set(null);
    this.touch(Date.now());
    this.keepAlive(Date.now());
  }

  /**
   * Single exit point. `expired` means the server already rejected the session, so no logout call
   * is made; otherwise the BE session is revoked. Other tabs are told to leave too.
   */
  async end(reason: SessionEndReason, opts: { fromOtherTab?: boolean } = {}): Promise<void> {
    if (this.ending) return;
    this.ending = true;
    try {
      this.stop();
      if (reason === 'expired' || opts.fromOtherTab) {
        this.auth.clearSession();
      } else {
        await this.auth.logout();
      }
      if (!opts.fromOtherTab) this.channel?.postMessage({ reason });
      await this.router.navigateByUrl('/login');
      const message = END_MESSAGES[reason];
      if (message) this.toast.show(this.translate.instant(message), 'info');
    } finally {
      this.ending = false;
    }
  }

  private start(): void {
    if (this.timer) return;
    const now = Date.now();
    this.lastActivity = now;
    this.lastKeepAlive = now;
    this.persistActivity(now);
    ACTIVITY_EVENTS.forEach((type) =>
      document.addEventListener(type, this.onActivity, { passive: true, capture: true }),
    );
    this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  private stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this._warningSecondsLeft.set(null);
    ACTIVITY_EVENTS.forEach((type) =>
      document.removeEventListener(type, this.onActivity, { capture: true }),
    );
  }

  private recordActivity(): void {
    // While "¿Sigues ahí?" is shown only an explicit answer counts (moving towards it must not).
    if (this._warningSecondsLeft() !== null) return;
    const now = Date.now();
    if (now - this.lastActivity < ACTIVITY_THROTTLE_MS) return;
    this.touch(now);
    if (now - this.lastKeepAlive >= this.keepAliveMs) this.keepAlive(now);
  }

  private tick(): void {
    const lastSeen = Math.max(this.lastActivity, this.readSharedActivity());
    const remaining = this.timeoutMs - (Date.now() - lastSeen);
    if (remaining <= 0) {
      void this.end('idle');
    } else if (remaining <= this.warningMs) {
      this._warningSecondsLeft.set(Math.ceil(remaining / 1000));
    } else if (this._warningSecondsLeft() !== null) {
      // The user came back in another tab.
      this._warningSecondsLeft.set(null);
    }
  }

  private touch(now: number): void {
    this.lastActivity = now;
    this.persistActivity(now);
  }

  private keepAlive(now: number): void {
    this.lastKeepAlive = now;
    this.auth.refresh().catch(() => void this.end('expired'));
  }

  // Activity is shared through localStorage so being active in one tab keeps every tab alive.
  private persistActivity(now: number): void {
    try {
      localStorage.setItem(ACTIVITY_KEY, String(now));
    } catch {
      // Storage unavailable (private mode): this tab still tracks its own activity.
    }
  }

  private readSharedActivity(): number {
    try {
      return Number(localStorage.getItem(ACTIVITY_KEY)) || 0;
    } catch {
      return 0;
    }
  }
}
